import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ChevronLeft } from 'lucide-react';

import noSeasonsPng from '@/assets/illustrations/empty/no-seasons.png';

import { useSeasonStore } from '@/stores/season-store';
import { daysUntilOpener, seasonStatus } from '@/utils/countdown-utils';
import { getStateName } from '@/utils/us-states';
import type { Season } from '@/types/season';
import { Button } from '@/components/common/Button';
import { OperationalGuide } from '@/components/common/OperationalGuide';

function seasonTypeLabel(seasonType: string): string {
  const map: Record<string, string> = {
    gun: 'Gun',
    bow: 'Bow',
    muzzleloader: 'Muzzleloader',
    crossbow: 'Crossbow',
    youth: 'Youth',
    other: 'Other',
  };
  return map[seasonType] ?? seasonType;
}

function SeasonCard({ season }: { season: Season }) {
  const status = seasonStatus(season);
  const days = daysUntilOpener(season.openerDate);

  const statusPill = {
    upcoming: 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)] ring-1 ring-[var(--color-accent)]/30',
    active: 'bg-[var(--color-done-bg)] text-[var(--color-done)] ring-1 ring-[var(--color-done)]/30',
    ended: 'bg-[var(--color-elevated)] text-[var(--color-text-muted)] ring-1 ring-[var(--color-border)]',
  }[status];

  const statusLabel = {
    upcoming: days === 0 ? 'Opens Today' : `${days} days away`,
    active: 'In Progress',
    ended: 'Ended',
  }[status];

  return (
    <Link
      to={`/seasons/${season.id}`}
      data-testid="season-card"
      className="block bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4 hover:border-[var(--color-border-subtle)] hover:bg-[var(--color-elevated)] transition-colors duration-100"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1 min-w-0">
          <span
            data-typography="display"
            className="font-display text-body-md font-medium text-[var(--color-text-display)] truncate"
          >
            {season.name}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-body-xs text-[var(--color-text-muted)] font-medium">
              {getStateName(season.state)} · {seasonTypeLabel(season.seasonType)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {season.isPrimary && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-body-xs font-medium bg-[var(--color-accent-subtle)] text-[var(--color-accent)] ring-1 ring-[var(--color-accent)]/30"
              data-testid="season-card-primary-badge"
            >
              Primary
            </span>
          )}
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-body-xs font-medium ${statusPill}`}
          >
            {statusLabel}
          </span>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-3 text-body-sm text-[var(--color-text-secondary)]">
        <span>Opens {format(parseISO(season.openerDate), 'MMM d, yyyy')}</span>
        {season.closerDate && (
          <>
            <span className="text-[var(--color-border)]">·</span>
            <span>Closes {format(parseISO(season.closerDate), 'MMM d, yyyy')}</span>
          </>
        )}
      </div>
    </Link>
  );
}

export function SeasonsPage() {
  const navigate = useNavigate();
  const { seasons, isLoading, loadSeasons } = useSeasonStore();

  useEffect(() => {
    void loadSeasons();
  }, [loadSeasons]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-[var(--color-text-muted)] text-body-sm">Loading seasons...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="seasons-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
            data-testid="back-button"
            className="flex items-center justify-center -ml-1 p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <h1 className="font-display text-display-lg text-[var(--color-text-display)]">Seasons</h1>
        </div>
        {seasons.length > 0 && (
          <Link to="/seasons/new">
            <Button variant="primary" data-testid="season-add-button">Add Season</Button>
          </Link>
        )}
      </div>

      {seasons.length === 0 ? (
        <OperationalGuide
          illustration={noSeasonsPng}
          heading="Set Up Your Season"
          subtext="Configure your season dates to align with state DNR regulations."
          ctaLabel="Add Season"
          ctaLink="/seasons/new"
          data-guide-id="seasons-empty"
        />
      ) : (
        <div className="flex flex-col gap-3">
          {seasons.map((season) => (
            <SeasonCard key={season.id} season={season} />
          ))}
        </div>
      )}
    </div>
  );
}
